import type {
  DehydratedViewportsConfigurations,
  StudyGroup,
} from '../ViewportsConfigurations/types';
import omit from 'ramda/es/omit';
import { useCallback, useEffect, useMemo } from 'react';
import { useRecoilCallback, useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import analytics from 'modules/analytics';
import { viewer } from 'modules/analytics/constants';
import { useSplitFlag, FF } from 'modules/feature-flags';
import {
  isAtLeastPhase4,
  isPhase4Steps,
} from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/constants';
import { useStudies, useBaseViewerData } from 'domains/viewer/Viewer/StudyLoader/useStudies';
import {
  useConfirmedHangingProtocolConfiguration,
  usePreviewHangingProtocolConfiguration,
} from './usePreviewHangingProtocol';
import type { GetHangingProtocolMatchQuery } from 'generated/graphql';
import { dehydratedHangingProtocol } from 'domains/viewer/ViewportsConfigurations';
import type { ViewportsConfigurations } from 'domains/viewer/ViewportsConfigurations';
import type { Layouts } from '../Viewer/StudyLoader/viewerLoaderState';
import { layoutsForAllCasesState } from '../Viewer/StudyLoader/viewerLoaderState';
import {
  useViewportsConfigurationsSelector,
  hangingProtocolLayoutsState,
  activeHangingProtocolSmidState,
} from '../ViewportsConfigurations/state';
import { ViewType } from 'generated/graphql';
import { logger } from 'modules/logger';
import { hydrateViewportsConfigurations } from '../ViewportsConfigurations/manipulators';
import { WithApolloContext } from 'modules/Apollo/types';
import {
  hangingProtocolStepIndex,
  hangingProtocolSubStepIndex,
  steppedHPConfigurations,
  clinicalStepsBaseline,
  getStepsFromVersioned,
  makeVersionedSteps,
} from './state';
import { stepFromGqlFormat } from './steppedHPTypes';
import { mergeUnsavedStepEdits } from './hooks/useClinicalStepDirtyState';
import type { StepConfiguration } from './steppedHPTypes';
import { rebuildHPFromStep } from './stepUtils';
import type { HangingProtocol } from './stepUtils';
import { hpInvalidationChannel, type HPInvalidationMessage } from './broadcastHPInvalidation';
import { useViewerId } from 'hooks/useViewerId';
import { useCurrentCaseId } from 'hooks/useCurrentCase';

// Re-export for backward compatibility with existing consumers
export { broadcastHPInvalidation } from './broadcastHPInvalidation';
// Re-export for backward compatibility with existing consumers
export type { HangingProtocol } from './stepUtils';
export { rebuildHPFromStep } from './stepUtils';

// Module-level variable to track analytics across all hook instances.
// This prevents duplicate analytics events when multiple components call useHangingProtocol().
// The key combines hanging protocol smid and first study smid to ensure analytics fire
// when the same protocol is applied to different cases.
// Exported for testing purposes only.
export let __lastTrackedHangingProtocolKey: string | null = null;

// Reset function for tests
export const __resetLastTrackedHangingProtocolKey = (): void => {
  __lastTrackedHangingProtocolKey = null;
};

// Module-level guard tracking the last (case + HP smid) combination whose steps were initialized.
// Keyed by BOTH case and HP smid because the same HP smid can match different cases: keying on the
// HP smid alone let a navigation to a new case matching the same HP skip re-init and keep stale
// step state (VX-4429). Must be module-level (not a ref) because the component can unmount/remount
// during step navigation, which would reset a ref and allow the init effect to re-run, overwriting
// user edits (e.g. renames) with the original GQL data.
let __lastInitializedHPKey: string | null = null;

// Reset function for tests. Clears the step-index homing key too: the two are set in the same pass,
// so a test that reset only the init guard would replay an "already homed" verdict from an earlier
// test in the same file and never see the step index snap to 0.
export const __resetLastInitializedHPKey = (): void => {
  __lastInitializedHPKey = null;
  __lastStepIndexHomedHPKey = null;
};

// Last case+HP key the step index was homed to step 0 for. Deliberately NOT cleared by
// invalidateHPInitializationCache: it is what tells a genuine protocol change (case transition,
// preview accept, switch, delete-to-default) apart from a re-read of the protocol already applied.
// The init guard above cannot answer that -- every caller that invalidates it by hand erases the
// identity along with it -- and homing to 0 on a re-read stomps whatever step the user is on by the
// time the response lands (VX-4783).
let __lastStepIndexHomedHPKey: string | null = null;

// Module-level guard for the activeHangingProtocolSmidState writer effect below. Tracks the last
// case+smid key written so a transient null match (e.g. right after a clinical step delete
// triggers a refetch) doesn't cancel in-flight operations gated on hpSmid -- see the effect's own
// comment for why. Must be module-level (not a ref) for the same reason as __lastInitializedHPKey:
// multiple useHangingProtocol() instances can be mounted at once (widget, layouts, viewport
// overlays), and this guard needs to be resettable from outside any single instance (VX-4615 review).
let __lastHPSmidKey: string | null = null;

// Reset function for tests
export const __resetLastHPSmidKey = (): void => {
  __lastHPSmidKey = null;
};

/**
 * Reset the smid-write guard so the next render's smid (including a legitimate transition to
 * null, e.g. deleting the currently-active HP with no other match) is written instead of being
 * mistaken for a transient blip. Call this anywhere the HP data is invalidated -- alongside
 * invalidateHPInitializationCache() -- so a delete of the active HP doesn't leave
 * activeHangingProtocolSmidState stuck pointing at the now-nonexistent HP.
 */
export const invalidateHPSmidGuard = (): void => {
  __lastHPSmidKey = null;
};

// Set when the init guard is invalidated by a mutation elsewhere (this window or, via
// broadcastHPInvalidation, another viewer window), to the hpKey that was active at that moment. It
// tells the next initialization pass that IF it is re-reading that same HP, unsaved step edits must
// be carried across rather than replaced by the server's copy (VX-4739).
//
// Scoped to the hpKey rather than a bare boolean: invalidateHPInitializationCache() is also called
// on HP delete, HP form submit (create or switch), and Reset All -- all legitimate transitions to a
// DIFFERENT hpKey. A bare flag would tell the next init to preserve "unsaved edits" from the HP the
// user just left, and mergeUnsavedStepEdits's positional fallback (needed because a whole-HP update
// still doesn't return every server-assigned smid in every case -- see VX-4741) could then splice
// those stale edits onto an unrelated HP's steps whenever the two happened to have the same step
// count. Comparing hpKey ensures only a genuine re-read of the SAME HP (a reorder, a save, or a
// same-HP broadcast from the other viewer window) preserves anything.
//
// A fresh page load never calls invalidateHPInitializationCache, so this stays null and a reload
// still starts from the server -- an unsaved edit is correctly discarded there (VX-4624).
let __preserveUnsavedStepEditsForHPKey: string | null = null;

// Reset function for tests
export const __resetPreserveUnsavedStepEditsFlag = (): void => {
  __preserveUnsavedStepEditsForHPKey = null;
};

// Smid of the hanging protocol the user just deleted, per window. Deleting an HP clears the step
// atoms, but it does not stop the queries already in flight for it, nor the independent background
// refetch usePrecacheQuery schedules, nor Apollo's previousData masking. Any of those can resolve
// afterwards still describing the deleted HP, and because the delete also invalidates the init
// guard, that late response sails through and re-populates steppedHPConfigurations -- the step bar
// comes back and stays until a reload (VX-4738). It shows up most often on viewer/1, whose only
// knowledge of the deletion arrives over the invalidation broadcast.
//
// Recording the smid lets the init effect drop those responses. Held until the next deletion
// replaces it; backend smids are UUIDs and a deleted HP never returns, so there is nothing to
// clear it for, and a page load starts from null anyway.
let __deletedHPSmid: string | null = null;

/** Reset function for tests. */
export const __resetDeletedHPSmid = (): void => {
  __deletedHPSmid = null;
};

/**
 * Record that a hanging protocol was deleted so this window ignores any later response that still
 * describes it. Call in the delete path, and on every other window via the invalidation broadcast.
 */
export const markHangingProtocolDeleted = (smid: Nullable<string>): void => {
  __deletedHPSmid = smid ?? null;
};

/**
 * Reset the HP initialization guard so the next refetch re-processes the HP
 * data even if the key hasn't changed (e.g. after a create/update mutation).
 */
export const invalidateHPInitializationCache = (): void => {
  // Capture the hpKey this invalidation is happening FOR (the one just initialized) before
  // clearing it, so the next init can tell a same-HP re-read from a switch to a different HP.
  __preserveUnsavedStepEditsForHPKey = __lastInitializedHPKey;
  __lastInitializedHPKey = null;
};

export type UseHangingProtocol = {
  loadingHangingProtocol: boolean;
  hangingProtocol: Nullable<HangingProtocol>;
  hangingProtocolViewportsConfigurations: Nullable<ViewportsConfigurations>;
  hangingProtocolLayouts: Nullable<Layouts>;
  resetToHP: () => void;
};

const useMatchedHangingProtocol = ({
  groupedStudies,
  loading,
  studyDescriptions,
  cases,
  context,
}: {
  cases: Array<Array<string>>;
  groupedStudies: Array<StudyGroup>;
  loading: boolean;
  studyDescriptions: Array<string>;
} & WithApolloContext): [
  GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'],
  boolean,
] => {
  // `gbvdLoading` is GBVD's load state; `hangingProtocolLoading` is the
  // standalone HP-match query's. Adding/removing a prior re-fires only the HP
  // query (GBVD vars are stable), so we must surface its loading too —
  // otherwise the HP/layout UI renders as settled while the match is in flight.
  const {
    hangingProtocol,
    loading: gbvdLoading,
    hangingProtocolLoading,
  } = useBaseViewerData({ context });
  return [hangingProtocol, gbvdLoading || hangingProtocolLoading];
};

export const useHangingProtocol = ({ context }: WithApolloContext): UseHangingProtocol => {
  const viewerId = useViewerId();
  const isPrimaryWindow = viewerId === '0';
  const currentCaseId = useCurrentCaseId();
  const previewedHangingProtocol = usePreviewHangingProtocolConfiguration();
  const confirmedHangingProtocol = useConfirmedHangingProtocolConfiguration();
  const { loading, groupedStudies, refetch, invalidateAllPrecachedStudies } = useStudies({
    context,
  });
  const [phase] = useSplitFlag(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  // Gate all Stepped-HP behavior on the phase-4-steps treatment. The step UI (StepBar,
  // useClinicalStepMode) is already gated on this phase; the hook must gate too,
  // otherwise a phase-4-steps-off user opening an HP that has step data would still get
  // the viewer rebuilt from step 0 (Split treatments are per-user/clinic, so
  // stepped HPs can exist in the DB while a given user's phase is not phase-4-steps).
  const isHPStepsEnabled = isPhase4Steps(phase);
  const [stepIndex, setStepIndex] = useRecoilState(hangingProtocolStepIndex);

  // Stepped HP state - will be used when backend returns steps format
  const setSteppedHPConfigurations = useSetRecoilState(steppedHPConfigurations);
  const liveStepConfigurationsVersioned = useRecoilValue(steppedHPConfigurations);
  const liveStepConfigurations = getStepsFromVersioned(liveStepConfigurationsVersioned);
  const setSubStepIndex = useSetRecoilState(hangingProtocolSubStepIndex);

  /**
   * Writes a freshly fetched step list into the viewer atoms, keeping steps the user has edited but
   * not saved (VX-4739). Reads both atoms synchronously so the merge sees the current values rather
   * than whatever this render closed over.
   */
  const applyFetchedSteps = useRecoilCallback(
    ({ snapshot, set }) =>
      (
        stepConfigs: Nullable<StepConfiguration[]>,
        hpName: Nullable<string>,
        preserveUnsavedEdits: boolean
      ) => {
        const previousVersioned = snapshot.getLoadable(steppedHPConfigurations).getValue();
        const previousSteps = getStepsFromVersioned(previousVersioned);
        const previousBaseline = snapshot.getLoadable(clinicalStepsBaseline).getValue();

        const mergedSteps = preserveUnsavedEdits
          ? mergeUnsavedStepEdits(stepConfigs ?? null, previousSteps, previousBaseline)
          : (stepConfigs ?? null);
        set(steppedHPConfigurations, (prev) => makeVersionedSteps(mergedSteps, prev, hpName));
        set(clinicalStepsBaseline, stepConfigs ? structuredClone(stepConfigs) : null);
      },
    []
  );

  const studyDescriptions: string[] = useMemo(
    () =>
      groupedStudies.flatMap((studies) =>
        studies.reduce<string[]>(
          (studies, { description }) => (description != null ? [...studies, description] : studies),
          []
        )
      ),
    [groupedStudies]
  );
  const cases = useMemo(() => {
    return groupedStudies.map((group) => group.map((study) => study.smid));
  }, [groupedStudies]);

  const [matchedHangingProtocol, matchedHangingProtocolLoading] = useMatchedHangingProtocol({
    groupedStudies,
    loading,
    studyDescriptions,
    cases,
    context,
  });

  const originalHangingProtocol = useMemo(
    () => previewedHangingProtocol ?? confirmedHangingProtocol ?? matchedHangingProtocol,
    [matchedHangingProtocol, previewedHangingProtocol, confirmedHangingProtocol]
  );

  useEffect(() => {
    // Feature-flag gate: with Stepped HP off, never process step data so the
    // viewer atoms (steppedHPConfigurations, stepIndex) stay at their flat-HP
    // defaults and production behavior is unchanged. Clear the init guard so
    // steps re-initialize cleanly if the flag flips on mid-session (Split cohort
    // change without a reload); otherwise a stale smid in the guard would make
    // the later flag-on pass skip initialization.
    if (!isHPStepsEnabled) {
      __lastInitializedHPKey = null;
      return;
    }

    const hpSmid = originalHangingProtocol?.smid ?? null;

    // A response for an HP that was deleted is stale by definition -- initializing from it would
    // rebuild the step bar for an HP that no longer exists (VX-4738). Leave the init guard alone
    // so the next response, for whichever HP actually matches now, still initializes normally.
    if (hpSmid != null && hpSmid === __deletedHPSmid) return;

    // Key the guard on case + HP smid. The same HP smid can match different cases, so keying on the
    // smid alone would skip re-init when navigating to a new case that matches the same HP, leaving
    // stale step state and a possibly out-of-bounds stepIndex (VX-4429). The '__no_hp__' segment
    // also serves as the null sentinel so a deleted/absent HP still produces a stable key.
    const hpKey = `${currentCaseId ?? '__no_case__'}:${hpSmid ?? '__no_hp__'}`;

    // Only initialize when this exact case+HP changes, not on every re-render. This prevents
    // overwriting user edits (e.g. renames) with the original GQL data.
    if (hpSmid != null && hpKey === __lastInitializedHPKey) {
      // The guard above protects `.steps` from a refetch clobbering in-progress local step
      // edits, but a plain rename doesn't touch `.steps` at all, so it must not be blocked by
      // that protection. Sync just the name here (in the same effect, so there's no risk of
      // this racing the full re-init below within the same render). Without this, a window
      // that didn't perform the rename itself (e.g. viewer/1 picking up a broadcast-triggered
      // refetch) would fetch the correct name from the server but keep showing the stale one,
      // since the guard above always blocks re-running for a same-smid update.
      const newName = originalHangingProtocol?.name ?? null;
      if (newName != null) {
        setSteppedHPConfigurations((prev) =>
          prev.name === newName ? prev : makeVersionedSteps(prev.steps, prev, newName)
        );
      }
      return;
    }
    // Skip re-initialization when the HP is temporarily null (e.g. during a
    // refetch cycle). Without this guard, a refetch that briefly returns null
    // would clear steppedHPConfigurations then re-populate them, causing a
    // flash of empty step chips in dual-window mode.
    if (hpSmid == null && __lastInitializedHPKey != null) {
      return;
    }
    __lastInitializedHPKey = hpKey;

    // Convert GQL array-format steps to internal Record-based format.
    // Treat empty arrays as null so flat HPs don't present as stepped.
    const rawStepConfigs = (originalHangingProtocol?.steps ?? []).map(stepFromGqlFormat);
    const stepConfigs = rawStepConfigs.length > 0 ? rawStepConfigs : null;

    // Keep any step the user has edited but not saved when this pass is a re-read of the SAME HP
    // the session already had (a mutation here or in the other viewer window invalidated the guard
    // for this hpKey). Order and membership still come from the server, so a reorder lands; only
    // the content of steps with unsaved edits is kept, and the baseline is always the server's
    // copy, which is what makes those preserved edits still read as dirty (VX-4739).
    //
    // Comparing hpKey (not just "was the flag set") is what keeps a switch to a different HP --
    // delete falling back to Default, form submit creating/switching HPs, Reset All -- from
    // adopting stale edits left over from the HP the user just left. A fresh page load never sets
    // the flag at all, so a reload still starts from the server and an unsaved edit is correctly
    // discarded (VX-4624).
    const preserveUnsavedEdits = hpKey === __preserveUnsavedStepEditsForHPKey;
    __preserveUnsavedStepEditsForHPKey = null;
    applyFetchedSteps(stepConfigs, originalHangingProtocol?.name ?? null, preserveUnsavedEdits);

    // Every HP change (case transition, preview accept, etc.) should land on
    // the first step. Without this, leftover stepIndex from the previous HP
    // survives — e.g., navigating from an 8-step MR on step 4 to a 10-step
    // MR keeps you on step 4 instead of showing step 0 as active.
    //
    // Only for an actual change, though. Reset All, a delete broadcast and a create all invalidate
    // the init guard by hand, so this effect re-runs for the protocol that is already applied, and
    // it runs whenever that response lands -- which can be several hundred milliseconds after the
    // UI has settled and the user has clicked a step. Homing to 0 there silently threw the click
    // away, and the step only stuck on a second click, once no response was still in flight
    // (VX-4783). A re-read keeps the user where they are; the callers that do want step 0 (Reset
    // All via revertAllStepsToBaseline) set it themselves.
    if (hpKey !== __lastStepIndexHomedHPKey) {
      __lastStepIndexHomedHPKey = hpKey;
      setStepIndex(0);
      setSubStepIndex(0);
      return;
    }

    // Same protocol re-read: the only index change owed here is a clamp, in case the response
    // describes fewer steps than the index points at (a step deleted in another window).
    //
    // A clamp lands on a different step, and sub-step indices are per-step -- leaving subStepIndex
    // where it was would point it at sub-steps the new step does not have. Reset it alongside, the
    // way the home-to-0 branch above does.
    const stepCount = stepConfigs?.length ?? 0;
    if (stepCount > 0 && stepIndex >= stepCount) {
      setStepIndex(stepCount - 1);
      setSubStepIndex(0);
    }
  }, [
    isHPStepsEnabled,
    originalHangingProtocol,
    currentCaseId,
    stepIndex,
    setStepIndex,
    setSteppedHPConfigurations,
    applyFetchedSteps,
    setSubStepIndex,
  ]);

  // Listen for cross-window HP invalidation signals. When viewer/0 saves or
  // deletes an HP, it broadcasts so viewer/1 can refetch and re-initialize.
  const handleHPInvalidation = useCallback(
    async (deletedHPSmid?: string) => {
      // Record the deletion before refetching: this window's own in-flight and background queries
      // can still resolve with the deleted HP, and the init guard is about to be cleared (VX-4738).
      if (deletedHPSmid != null) markHangingProtocolDeleted(deletedHPSmid);
      await invalidateAllPrecachedStudies();
      await refetch();
      // Drop the guards only once the fresh protocol is in hand.
      //
      // Clearing them first opened a window hundreds of milliseconds wide -- the length of the
      // refetch -- in which this window would re-render with the guard down and the OLD protocol
      // still in `originalHangingProtocol`. The init effect then re-applied that stale copy and,
      // because every write bumps the shared version counter, broadcast it as newer than the fresh
      // steps the saving window had just sent us. The saving window took it, and the steps the user
      // had just added disappeared from both windows until a refresh (VX-4770).
      //
      // Measured: v/1 accepted v6/3steps, then re-initialised from its stale 1-step copy and emitted
      // v7/1steps, which v/0 accepted over its own correct v6/3steps.
      invalidateHPInitializationCache();
      invalidateHPSmidGuard();
    },
    [invalidateAllPrecachedStudies, refetch]
  );

  useEffect(() => {
    const handler = (message?: HPInvalidationMessage) => {
      // handleHPInvalidation is async; catch so a rejected refetch doesn't surface
      // as an unhandled promise rejection from the BroadcastChannel callback.
      handleHPInvalidation(message?.deletedHPSmid).catch((error) => {
        logger.warn('Failed to handle hanging-protocol invalidation broadcast', { error });
      });
    };
    // Use addEventListener (not onmessage) so multiple concurrent useHangingProtocol instances
    // (HangingProtocolWidget, useLayouts, viewport overlays) each register their own handler.
    // Assigning the single onmessage slot in each instance clobbered the others' handlers, so
    // cross-window HP invalidation could be silently dropped (VX-4430).
    hpInvalidationChannel.addEventListener('message', handler);
    return () => {
      hpInvalidationChannel.removeEventListener('message', handler);
    };
  }, [handleHPInvalidation]);

  // For stepped HPs, the GQL response has empty top-level data.layouts and
  // viewportsConfigurations (they live per-step instead). This memo replaces
  // those fields with the current step's values so downstream consumers
  // (getHangingProtocolLayouts, dehydrateHangingProtocol, etc.) work unchanged.
  // Uses liveStepConfigurations (Recoil atom) so renames and edits are reflected.
  const hangingProtocol = useMemo(() => {
    // Feature-flag gate (critical): with Stepped HP off, always return the
    // original (flat) HP so the viewer layout/viewports are never rebuilt from
    // step data. This is the definitive production-safety guard -- everything
    // downstream (dehydrateHangingProtocol, getHangingProtocolLayouts, the
    // dehydratedHangingProtocol atom write) derives from this value.
    if (!isHPStepsEnabled) {
      return originalHangingProtocol;
    }

    const stepConfigs = liveStepConfigurations;
    if (stepConfigs == null || stepConfigs.length === 0 || stepIndex >= stepConfigs.length) {
      return originalHangingProtocol;
    }

    // Guard against a cross-window step echo (steppedHPConfigurations syncs via
    // BroadcastChannel) arriving before this window's own HP query resolves.
    // Rebuilding from a null HP would produce an smid-less object and transiently
    // null activeHangingProtocolSmidState.
    if (originalHangingProtocol == null) {
      return originalHangingProtocol;
    }

    const stepConfig = stepConfigs[stepIndex];
    const rebuilt = rebuildHPFromStep(originalHangingProtocol, stepConfig);

    return rebuilt;
  }, [isHPStepsEnabled, originalHangingProtocol, liveStepConfigurations, stepIndex]);

  const hangingProtocolViewportsConfigurations = useMemo(() => {
    return dehydrateHangingProtocol(hangingProtocol);
  }, [hangingProtocol]);

  const setHangingProtocol = useRecoilCallback(
    ({ set }) =>
      (caseSmid: Nullable<string>, value: Nullable<DehydratedViewportsConfigurations>) => {
        if (caseSmid == null) return;
        set(dehydratedHangingProtocol(caseSmid), value);
      },
    []
  );
  const setHangingProtocolSmid = useSetRecoilState(activeHangingProtocolSmidState);
  useEffect(() => {
    // The HP widget only renders on the primary window (viewer/0). Only the primary
    // window should write to broadcast atoms to avoid infinite ping-pong between windows.
    // The secondary window receives values via broadcastChannelSynchronizerEffect.
    if (!isPrimaryWindow) return;
    // Note: This *should* be fine. Null is the default, but should not occur again after the initial query. Putting this gate fixes tricky issues with TestViewportWrapper.
    if (hangingProtocolViewportsConfigurations == null) return;

    setHangingProtocol(currentCaseId, hangingProtocolViewportsConfigurations);
  }, [isPrimaryWindow, currentCaseId, setHangingProtocol, hangingProtocolViewportsConfigurations]);

  // Set the HP smid independently of the dehydrated viewport configs.
  // The smid is available from hangingProtocol immediately, but viewport
  // configs may be delayed (async step rebuild, hydration). Gating the
  // smid on viewport configs caused createStepOnBackend and saveStepOrder
  // to silently skip backend calls (they guard on hpSmid being non-null).
  //
  // Like the effect above, only the primary window should write to this
  // broadcast atom — secondary windows consume it via
  // broadcastChannelSynchronizerEffect. Writing from both windows increases
  // cross-window chatter and can reintroduce multi-window race conditions.
  //
  // A getBaseViewerData refetch that transiently returns no matched HP (e.g. right after
  // a clinical step delete) would otherwise null this out immediately -- unlike the
  // BroadcastChannel path, which already rejects stale null echoes via its mergeFn (see
  // activeHangingProtocolSmidState in ViewportsConfigurations/state.ts), this local writer had
  // no equivalent guard. That let a single transient null cancel any in-flight operation
  // gated on hpSmid, e.g. the deferred step-deletion timer in useClinicalStepOperations. Skip a
  // null for the *same case* (almost certainly a transient refetch blip); a genuine case switch
  // to a no-HP case still clears it, since the case segment of the key changes.
  //
  // Deleting the currently-active HP is the one case where a same-case null IS legitimate (the
  // HP no longer exists, so the refetch's null/default-HP result must win). That's handled by
  // invalidateHPSmidGuard(), called alongside invalidateHPInitializationCache() wherever HP data
  // is invalidated (see the delete handler in HangingProtocolWidget.tsx and
  // handleHPInvalidation above) -- it clears __lastHPSmidKey so the next null is written instead
  // of skipped (VX-4615 review).
  useEffect(() => {
    if (!isPrimaryWindow) return;
    const smid = hangingProtocol?.smid ?? null;
    const caseKeyPrefix = `${currentCaseId ?? '__no_case__'}:`;
    if (smid == null && __lastHPSmidKey?.startsWith(caseKeyPrefix)) {
      return;
    }
    __lastHPSmidKey = `${caseKeyPrefix}${smid ?? '__no_hp__'}`;
    setHangingProtocolSmid(smid);
  }, [isPrimaryWindow, setHangingProtocolSmid, hangingProtocol?.smid, currentCaseId]);

  useEffect(() => {
    const hpSmid = hangingProtocol?.smid ?? null;
    const primaryStudySmid = groupedStudies[0]?.[0]?.smid ?? null;
    const trackingKey =
      primaryStudySmid != null && hpSmid != null ? `${primaryStudySmid}:${hpSmid}` : null;

    if (
      !isPhase4 ||
      trackingKey == null ||
      trackingKey === __lastTrackedHangingProtocolKey ||
      hangingProtocol == null
    ) {
      return;
    }

    const studies = groupedStudies.flat().map((study) => ({
      smid: study.smid,
      description: study.description ?? null,
      series_descriptions: study.seriesList?.map((s) => s.description).filter(Boolean) ?? [],
    }));

    analytics.track(viewer.usr.hangingProtocolApplied, {
      hanging_protocol_name: hangingProtocol.name ?? null,
      hanging_protocol_smid: hpSmid,
      has_owner: true,
      hanging_protocol_shape: {
        name: hangingProtocol.name ?? '',
        study_descriptions: hangingProtocol.studyDescriptions ?? [],
        number_of_priors: hangingProtocol.numberOfPriors ?? 0,
        study_criteria: hangingProtocol.studyCriteria ?? null,
        layouts: hangingProtocol.data?.layouts ?? [],
        viewports_configurations:
          hangingProtocol.viewportsConfigurations?.map((vc) => ({
            id: vc.id,
            view_type: vc.configuration.viewType,
            series_descriptions: vc.configuration.seriesDescriptions ?? [],
            series_criteria: vc.configuration.seriesCriteria ?? null,
            comparison_index: vc.configuration.comparisonIndex ?? null,
            matched_study_smid: vc.configuration.study?.smid ?? null,
            matched_series_smid: vc.configuration.series?.smid ?? null,
          })) ?? [],
      },
      studies,
    });

    __lastTrackedHangingProtocolKey = trackingKey;
  }, [hangingProtocol, isPhase4, groupedStudies]);

  const hangingProtocolLayouts: Nullable<Layouts> = useMemo(() => {
    return getHangingProtocolLayouts(hangingProtocol);
  }, [hangingProtocol]);

  const setHangingProtocolLayouts = useSetRecoilState(hangingProtocolLayoutsState);
  useEffect(() => {
    setHangingProtocolLayouts(hangingProtocolLayouts);
  }, [setHangingProtocolLayouts, hangingProtocolLayouts]);

  const viewportsConfigurationsRecoilSelector = useViewportsConfigurationsSelector({ context });
  const resetToHP = useRecoilCallback(
    ({ set, reset }) =>
      () => {
        // the selector calls set on the base atom, so reset is safe here
        reset(viewportsConfigurationsRecoilSelector);
        // Prefer set() over reset() as a defensive measure: reset() on atoms
        // with selector-based defaults produces a DefaultValue instance, which
        // broadcastChannelSynchronizerEffect silently skips. layoutsForAllCasesState
        // has a concrete default so reset() would work today, but set() is
        // resilient to future changes in the atom's default definition.
        // Scope the clear to the current case; layoutsForAllCasesState holds
        // every case's layout overrides, so clearing the whole map would wipe
        // out other cases' layouts too.
        set(layoutsForAllCasesState, (prev) =>
          currentCaseId != null ? omit([currentCaseId], prev) : prev
        );
      },
    [viewportsConfigurationsRecoilSelector, currentCaseId]
  );

  return {
    hangingProtocol,
    loadingHangingProtocol: matchedHangingProtocolLoading,
    hangingProtocolViewportsConfigurations:
      hangingProtocolViewportsConfigurations != null
        ? hydrateViewportsConfigurations(hangingProtocolViewportsConfigurations, groupedStudies)
        : undefined,
    hangingProtocolLayouts,
    resetToHP,
  };
};

function validateViewType(viewType?: ViewType | null): ViewType {
  // @ts-expect-error [EN-10263] - TS2345 - Argument of type 'ViewType | null | undefined' is not assignable to parameter of type 'ViewType'.
  if (!Object.values(ViewType).includes(viewType)) {
    logger.debug(`Invalid ViewType from Hanging Protocol viewType: ${viewType ?? 'null'}`);
  }
  return viewType ?? ViewType.TwoDDre;
}

export function dehydrateHangingProtocol(
  hangingProtocol: GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies']
): Nullable<DehydratedViewportsConfigurations> {
  return hangingProtocol?.viewportsConfigurations?.reduce((acc: any, { id, configuration }) => {
    return {
      ...acc,
      [id]: {
        ...configuration,
        viewType: validateViewType(configuration.viewType),
      },
    };
  }, {});
}

/**
 * Extracts layouts from a hanging protocol's `data.layouts` array.
 *
 * For stepped HPs the raw GQL response has `data.layouts: []` because each
 * step owns its own layouts. The `hangingProtocol` memo in `useHangingProtocol`
 * replaces `data.layouts` with the current step's layouts before this function
 * is called, so consumers always receive the correct value.
 */
export function getHangingProtocolLayouts(
  hangingProtocol: Nullable<
    GetHangingProtocolMatchQuery['matchHangingProtocolByWorklistsAndStudies'] | HangingProtocol
  >
): Nullable<Layouts> {
  // Guard the full `data.layouts` path, not just a null protocol: a hanging
  // protocol can arrive non-null but without populated layouts (e.g. a partial
  // or auth-skipped response now that the HP match is its own query). Treat that
  // like "no protocol" so callers fall back to DEFAULT_LAYOUTS rather than
  // crashing the viewer render.
  const layouts = hangingProtocol?.data?.layouts;
  if (layouts == null) return;

  return Object.fromEntries(
    layouts.map(({ id, layout }: { id: string; layout: readonly number[] }) => [
      id,
      [layout[0], layout[1]], // converting the GraphQL array to a tuple
    ])
  );
}
